import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Delete, Lock, ArrowLeft, Store } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../lib/auth";

export const STAFF_CODE_KEY = "jatak_staff_code";
const errText = (e) => (e.response ? apiError(e.response.data?.detail) || "Noget gik galt" : "Ingen forbindelse – prøv igen.");

function Keypad({ pin, setPin, onDone, busy }) {
  const press = (k) => {
    if (busy) return;
    if (k === "del") return setPin(pin.slice(0, -1));
    if (pin.length >= 4) return;
    const next = pin + k;
    setPin(next);
    if (next.length === 4) onDone(next);
  };
  useEffect(() => {
    const onKey = (e) => {
      if (/^\d$/.test(e.key)) press(e.key);
      else if (e.key === "Backspace") press("del");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  return (
    <div className="kp" data-testid="staff-keypad">
      <div className="kp-dots" aria-label={`${pin.length} af 4 cifre`} data-testid="staff-pin-dots">
        {[0, 1, 2, 3].map((i) => <i key={i} className={i < pin.length ? "on" : ""} />)}
      </div>
      <div className="kp-grid">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k) => (
          <button key={k} type="button" onClick={() => press(k)} data-testid={`staff-key-${k}`}>{k}</button>
        ))}
        <span />
        <button type="button" onClick={() => press("0")} data-testid="staff-key-0">0</button>
        <button type="button" className="kp-del" onClick={() => press("del")} aria-label="Slet" data-testid="staff-key-del"><Delete size={24} /></button>
      </div>
    </div>
  );
}

export function StaffLocked() {
  return (
    <div className="staff-locked" role="alert" data-testid="staff-locked-overlay">
      <Lock size={32} />
      <h2>Butikken har ikke adgang lige nu</h2>
      <p>Kontakt ejeren af butikken.</p>
    </div>
  );
}

export default function StaffLogin() {
  const { store, staffSignIn, logout } = useAuth();
  const nav = useNavigate();
  const [code, setCode] = useState(() => localStorage.getItem(STAFF_CODE_KEY) || "");
  const [list, setList] = useState(null);
  const [codeInput, setCodeInput] = useState("");
  const [username, setUsername] = useState("");
  const [picked, setPicked] = useState(null);
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!code) return;
    api.post("/staff-auth/list", { code }).then((r) => setList(r.data)).catch((e) => {
      if (e.response?.status === 404) { localStorage.removeItem(STAFF_CODE_KEY); setCode(""); }
      setErr(errText(e));
    });
  }, [code]);

  if (store?.staff) return <Navigate to="/pickup" replace />;

  const login = async (p) => {
    setBusy(true); setErr("");
    const c = code || codeInput.trim().toUpperCase();
    try {
      if (store) logout();
      const body = picked ? { code: c, staff_id: picked.id, pin: p } : { code: c, username, pin: p };
      const r = await api.post("/staff-auth/login", body);
      localStorage.setItem(STAFF_CODE_KEY, c);
      staffSignIn(r.data);
      nav("/pickup", { replace: true });
    } catch (e) {
      setErr(errText(e));
      setPin("");
    } finally { setBusy(false); }
  };
  const forget = () => { localStorage.removeItem(STAFF_CODE_KEY); setCode(""); setList(null); setPicked(null); setPin(""); setErr(""); };
  const remembered = code && list;

  return (
    <div className="staff-page" data-testid="staff-login-page">
      <header className="staff-top">
        <span className="as-brand"><img src="/logo.svg?v=2" alt="" width="30" height="30" /><span>JaTak Pro</span></span>
        <span className="staff-top-sub">Personale · Udlevering</span>
      </header>
      <main className="staff-main">
        {remembered && !picked && (
          <section className="staff-card" data-testid="staff-name-list">
            <p className="staff-store"><Store size={16} /> {list.store_name}</p>
            <h1>Hvem er du?</h1>
            {list.staff.length ? (
              <div className="staff-names">
                {list.staff.map((s) => (
                  <button key={s.id} type="button" onClick={() => { setPicked(s); setPin(""); setErr(""); }} data-testid={`staff-name-${s.id}`}>
                    <span className="staff-ini">{s.name.slice(0, 1).toUpperCase()}</span>{s.name}
                  </button>
                ))}
              </div>
            ) : <p className="muted">Der er ingen aktive medarbejdere. Bed ejeren oprette dig under Indstillinger.</p>}
            {err && <div className="notice error" role="alert" data-testid="staff-error">{err}</div>}
            <button type="button" className="staff-link" onClick={forget} data-testid="staff-switch-store">Ikke din butik? Skift butik</button>
          </section>
        )}
        {(picked || !code) && (
          <section className="staff-card" data-testid="staff-pin-step">
            {picked ? (
              <>
                <button type="button" className="staff-back" onClick={() => { setPicked(null); setErr(""); }} data-testid="staff-back"><ArrowLeft size={16} /> Vælg en anden</button>
                <h1>Hej {picked.name}</h1>
                <p className="muted">Skriv din PIN-kode</p>
              </>
            ) : (
              <>
                <h1>Log ind som medarbejder</h1>
                <div className="staff-fields">
                  <label>Butikskode<input value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())} maxLength={6} autoCapitalize="characters" autoComplete="off" placeholder="Fx BERG42" data-testid="staff-code-input" /></label>
                  <label>Brugernavn<input value={username} onChange={(e) => setUsername(e.target.value)} autoCapitalize="none" autoComplete="username" data-testid="staff-username-input" /></label>
                </div>
                <p className="muted">PIN-kode</p>
              </>
            )}
            {err && <div className="notice error" role="alert" data-testid="staff-error">{err}</div>}
            <Keypad pin={pin} setPin={setPin} busy={busy} onDone={(p) => (picked || (codeInput && username) ? login(p) : setErr("Udfyld butikskode og brugernavn."))} />
            {busy && <p className="muted" data-testid="staff-busy">Logger ind…</p>}
          </section>
        )}
        <p className="staff-foot"><a href="/login" data-testid="staff-owner-login">Ejer? Log ind med e-mail</a></p>
        <p className="staff-foot staff-help" data-testid="staff-help-note">
          Brug for hjælp? Kontakt din butiksejer ·{" "}
          <a href="/guide/JaTak-Pro-medarbejderguide.pdf" target="_blank" rel="noopener" data-testid="staff-guide-link">Sådan virker det</a>
        </p>
      </main>
    </div>
  );
}
